/* ---------------------------------------------------------------------------
   The rating equation and the posting decision.

   ONE EQUATION FOR THE WHOLE PROGRAM. Every LRLFM, LRASM, POSTLFM and POSTASM
   sheet in all sixteen workbooks computes the same thing:

       RF = (capacity - gammaD x dead) / (gammaLL x live)
       LR = RF x the vehicle's gross weight

   What changes between them is where the capacity came from and which load
   factors apply — and both of those are arguments here. LFM divides a factored
   ultimate capacity; ASM divides an allowable-stress capacity with unit factors.
   Nothing else differs, which is why one file replaces eight sheets per family.

   A RATING IS A TABLE, NOT A NUMBER. Four vehicles x two levels x two methods x
   however many sections the family checks. The governing value is a `min` over
   that table, and this module keeps the whole table rather than the minimum,
   because "the bridge rates 0.67" is not a report — "the bridge rates 0.67, on
   concrete allowable stress in midspan moment, under the inventory level" is.
   Every consumer here can say which cell governed.

   NO ROUNDING ON THE PATH. The rating sheets round the factor to two decimals
   and multiply the rounded value by the vehicle weight; the posting sheets do
   not, and the posting sheet is the one the sign is made from. The two disagree
   in the third significant figure (correction C9). Full precision throughout
   here, rounded once for display.
   --------------------------------------------------------------------------- */

import { round2 } from './round.ts';
import type { RatingLevel, RatingVehicle, StandardsProfile } from './profile.ts';
import { RATING_LEVELS } from './profile.ts';
import type { VehicleId } from './vehicles.ts';

/**
 * Which of the two methods produced a capacity.
 *
 * Load Factor and Allowable Stress. Both are computed for every RC bridge and
 * the worse governs — the reference RCFS passes LFM at 1.22 and fails ASM at
 * 0.67, and reporting only one would have posted the bridge wrongly either way.
 * SIG is ASM only; the 2009 program provides no LFM path for steel girders.
 */
export type Method = 'LFM' | 'ASM';

/**
 * One section, one action, with its capacity at both levels.
 *
 * `capacity` and `dead` must be in the same unit, and `live` in that unit too —
 * kN-m for a moment check, kN for a shear check. Per metre of width for a slab,
 * per girder for a girder. Nothing here converts anything.
 */
export interface Check {
  /** Stable id, e.g. `moment-midspan`, `shear-support`, `moment-pier`. */
  id: string;
  /** As the report names it, e.g. "Moment (midspan)". */
  label: string;
  method: Method;
  /** Capacity at each level. */
  capacity: Readonly<Record<RatingLevel, number>>;
  /** Unfactored dead load effect. */
  dead: number;
  /** Unfactored live load effect, with impact and distribution, per vehicle. */
  live: Readonly<Partial<Record<VehicleId, number>>>;
  /**
   * What set the capacity, where two candidates competed.
   *
   * ASM moment takes the lesser of the concrete and the reinforcement capacity,
   * and which one won is the single most useful sentence on a rating report: it
   * says whether adding steel would help. Empty where nothing competed.
   */
  governedBy?: string;

  /**
   * Load factors belonging to THIS CHECK rather than to the method.
   *
   * Added for PCDG's serviceability ratings, and deliberately narrow. Two of the
   * three are the ASM factors already — unity throughout — but the third,
   * `LRC!G15`, halves the dead-load stress before subtracting it:
   *
   *     RF = (0.4 f'c − ½(Fd + Fp + Fs)) / FL
   *
   * That 0.5 is part of what the check MEANS, not a departmental calibration of
   * the kind `profile.factors` carries, so it belongs on the check. Omit it and
   * the method's own factors apply, which is what every other family does.
   */
  factors?: { dead: number; live: Readonly<Record<RatingLevel, number>> };

  /**
   * Which levels this check actually distinguishes. Both, where omitted.
   *
   * CBPLR'S SERVICEABILITY RATING HAS NO INVENTORY/OPERATING DISTINCTION —
   * `LRSUM` prints one figure per serviceability row against two for every
   * strength row, because the allowable stress is the allowable stress. Emitting
   * such a check at both levels would put a number in the table that the program
   * does not compute and nobody could defend; recording it at `inventory` alone
   * keeps one row per rating the workbook actually produces. Nothing is lost
   * from `governing` — an identical second row could never be the minimum.
   */
  levels?: readonly RatingLevel[];
}

/** One cell of the rating table. */
export interface RatingFactor {
  check: string;
  checkLabel: string;
  method: Method;
  vehicle: VehicleId;
  level: RatingLevel;
  /** The rating factor. Dimensionless. */
  rf: number;
  /** RF x the vehicle's gross weight, tonnes. */
  lr: number;
  governedBy?: string;
}

/**
 * The rating equation.
 *
 * Returns null where the live load effect is zero or absent: a rating factor
 * with nothing in the denominator is not infinity, it is a question nobody
 * asked. Excel returns #DIV/0! here and the sheet shows an error; returning
 * null makes the caller say so rather than reporting an unbounded rating.
 */
export function ratingFactor(
  capacity: number,
  dead: number,
  live: number,
  deadFactor: number,
  liveFactor: number,
): number | null {
  const denominator = liveFactor * live;
  if (!Number.isFinite(denominator) || denominator === 0) return null;
  return (capacity - deadFactor * dead) / denominator;
}

/** Every vehicle a rating is expressed against — the rating truck and the posting trucks. */
export function allVehicles(profile: StandardsProfile): readonly RatingVehicle[] {
  return [profile.ratingVehicle, ...profile.postingVehicles];
}

function vehicleById(profile: StandardsProfile, id: VehicleId): RatingVehicle | null {
  return allVehicles(profile).find((v) => v.id === id) ?? null;
}

/**
 * The whole rating table for a set of checks.
 *
 * A check contributes a row per vehicle it carries a live load for and per
 * level. A vehicle with no live load recorded on a check is skipped rather than
 * rated at zero.
 */
export function ratingTable(
  checks: readonly Check[],
  profile: StandardsProfile,
): readonly RatingFactor[] {
  const rows: RatingFactor[] = [];
  for (const check of checks) {
    const factors =
      check.factors ?? (check.method === 'LFM' ? profile.factors.lfm : profile.factors.asm);
    const levels = check.levels ?? RATING_LEVELS;
    for (const [id, live] of Object.entries(check.live)) {
      if (live === undefined) continue;
      const vehicle = vehicleById(profile, id as VehicleId);
      if (!vehicle) continue;
      for (const level of levels) {
        const rf = ratingFactor(
          check.capacity[level],
          check.dead,
          live,
          factors.dead,
          factors.live[level],
        );
        if (rf === null) continue;
        rows.push({
          check: check.id,
          checkLabel: check.label,
          method: check.method,
          vehicle: vehicle.id,
          level,
          rf,
          lr: rf * vehicle.tonnes,
          ...(check.governedBy === undefined ? {} : { governedBy: check.governedBy }),
        });
      }
    }
  }
  return rows;
}

/** The worst row of a subset, or null where the subset is empty. */
export function governing(
  rows: readonly RatingFactor[],
  where: (r: RatingFactor) => boolean = () => true,
): RatingFactor | null {
  let worst: RatingFactor | null = null;
  for (const row of rows) {
    if (!where(row)) continue;
    if (worst === null || row.rf < worst.rf) worst = row;
  }
  return worst;
}

/* --------------------------- posting --------------------------- */

/**
 * What a posting sign should say for one vehicle.
 *
 * `POSTSUM` computes `MIN(weight, LR_asm, LR_lfm)` where each LR is itself the
 * lesser of the inventory and operating factors. So: the vehicle's own weight
 * unless the bridge cannot carry it, in which case the lowest rating across both
 * methods, both levels and every section checked.
 *
 * THE MINIMUM IS ALWAYS THE INVENTORY LEVEL in practice — the operating live
 * factor is smaller and the operating capacity larger, so operating always rates
 * higher. The min is taken over both anyway, faithfully, because that is what the
 * sheet does and a profile with unusual factors must not quietly change meaning.
 */
export interface Posting {
  vehicle: RatingVehicle;
  /** Tonnes. Equal to the vehicle's weight where the bridge carries it freely. */
  tonnes: number;
  /** True where the bridge carries this vehicle at full weight — no restriction. */
  unrestricted: boolean;
  /** The row that set the limit, or null where the weight itself governed. */
  governedBy: RatingFactor | null;
}

export function posting(
  rows: readonly RatingFactor[],
  profile: StandardsProfile,
): readonly Posting[] {
  return allVehicles(profile).map((vehicle) => {
    const worst = governing(rows, (r) => r.vehicle === vehicle.id);
    if (worst === null || worst.lr >= vehicle.tonnes) {
      return { vehicle, tonnes: vehicle.tonnes, unrestricted: true, governedBy: null };
    }
    return { vehicle, tonnes: worst.lr, unrestricted: false, governedBy: worst };
  });
}

/* --------------------------- the whole result --------------------------- */

export interface RatingResult {
  profile: { id: string; version: string };
  checks: readonly Check[];
  table: readonly RatingFactor[];
  /** The worst cell in the table. Null only where nothing could be rated. */
  governing: RatingFactor | null;
  /** Per-vehicle posting, rating truck first. */
  posting: readonly Posting[];
  /**
   * Whether the structure needs posting at all.
   *
   * True where any posting vehicle is restricted below its own weight — which
   * is `LRLFM!E20`'s test, `IF(MIN(RF) > 1, return, LOAD POSTING)`, expressed
   * on the answer rather than on one sheet's pair of factors.
   */
  postingRequired: boolean;
}

export function rate(checks: readonly Check[], profile: StandardsProfile): RatingResult {
  const table = ratingTable(checks, profile);
  const posted = posting(table, profile);
  return {
    profile: { id: profile.id, version: profile.version },
    checks,
    table,
    governing: governing(table),
    posting: posted,
    postingRequired: posted.some((p) => !p.unrestricted),
  };
}

/** Two decimals, the way a rating is written down. Display only. */
export function displayRf(rf: number): string {
  return round2(rf).toFixed(2);
}

/** Tonnes to one decimal, the way a posting sign is written. Display only. */
export function displayTonnes(tonnes: number): string {
  return tonnes.toFixed(1);
}
